import { jsx } from "react/jsx-runtime";
import { c } from "react-compiler-runtime";
import { useState, useEffect, useContext, createContext, useRef, useId } from "react";
import { TransportHookRegistry } from "../transport-hooks.js";
import { TransportHookContext } from "../transport-hooks.context.js";
import { useStore, create } from "zustand";
import { useShallow } from "zustand/shallow";
import { StorageAPI } from "@graphiql/toolkit";
import { createEditorSlice } from "../stores/editor.js";
import { visit, parse, isSchema, buildClientSchema, validateSchema } from "graphql";
import { STORAGE_KEY, DEFAULT_QUERY, DEFAULT_PRETTIFY_QUERY } from "../constants.js";
import { getDefaultTabState } from "../utility/tabs.js";
import { useMonaco } from "../stores/monaco.js";
import { useDidUpdate } from "../utility/hooks.js";
import { createStorageSlice } from "../stores/storage.js";
import { createExecutionSlice, isResponseView } from "../stores/execution.js";
import { createPluginSlice } from "../stores/plugin.js";
import { createSchemaSlice } from "../stores/schema.js";
import { createThemeSlice } from "../stores/theme.js";
function isIntrospectionData(value) {
  return typeof value === "object" && value !== null && "__schema" in value;
}
const GraphiQLContext = createContext(null);
const GraphiQLProvider = (props) => {
  const $ = c(5);
  if (!props.fetcher && !props.transport) {
    throw new TypeError("The `GraphiQLProvider` component requires either a `transport` or a `fetcher` prop.");
  }
  if (props.fetcher && props.transport) {
    throw new TypeError("The `fetcher` and `transport` props are mutually exclusive. Pass one or the other, not both.");
  }
  if (props.validationRules) {
    throw new TypeError("The `validationRules` prop has been removed. Use custom GraphQL worker, see https://github.com/graphql/graphiql/tree/main/packages/monaco-graphql#custom-webworker-for-passing-non-static-config-to-worker.");
  }
  if (props.query) {
    throw new TypeError("The `query` prop has been removed. Use `initialQuery` prop instead, or set value programmatically using:\n\nconst queryEditor = useGraphiQL(state => state.queryEditor)\n\nuseEffect(() => {\n  queryEditor.setValue(query)\n}, [query])");
  }
  if (props.variables) {
    throw new TypeError("The `variables` prop has been removed. Use `initialVariables` prop instead, or set value programmatically using:\n\nconst variableEditor = useGraphiQL(state => state.variableEditor)\n\nuseEffect(() => {\n  variableEditor.setValue(variables)\n}, [variables])");
  }
  if (props.headers) {
    throw new TypeError("The `headers` prop has been removed. Use `initialHeaders` prop instead, or set value programmatically using:\n\nconst headerEditor = useGraphiQL(state => state.headerEditor)\n\nuseEffect(() => {\n  headerEditor.setValue(headers)\n}, [headers])");
  }
  if (props.response) {
    throw new TypeError("The `response` prop has been removed. Set value programmatically using:\n\nconst responseEditor = useGraphiQL(state => state.responseEditor)\n\nuseEffect(() => {\n  responseEditor.setValue(response)\n}, [response])");
  }
  const {
    actions
  } = useMonaco();
  const [mounted, setMounted] = useState(false);
  let t0;
  if ($[0] !== actions) {
    t0 = () => {
      actions.initialize();
      setMounted(true);
    };
    $[0] = actions;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  useEffect(t0, t1);
  if (!mounted) {
    return null;
  }
  let t2;
  if ($[3] !== props) {
    t2 = /* @__PURE__ */ jsx(InnerGraphiQLProvider, { ...props });
    $[3] = props;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
const InnerGraphiQLProvider = (t0) => {
  const $ = c(31);
  const {
    defaultHeaders,
    defaultQuery: t1,
    defaultTabs,
    externalFragments,
    onEditOperationName,
    onTabChange,
    shouldPersistHeaders: t2,
    onCopyQuery,
    onSaveQuery,
    onPrettifyQuery: t3,
    customScalarSchemas,
    dangerouslyAssumeSchemaIsValid: t4,
    fetcher,
    transport,
    inputValueDeprecation: t5,
    introspectionQueryName: t6,
    onSchemaChange,
    schema,
    schemaDescription: t7,
    getDefaultFieldNames,
    operationName: t8,
    onTogglePluginVisibility,
    plugins: t9,
    referencePlugin,
    visiblePlugin,
    children,
    defaultTheme: t10,
    editorTheme: t11,
    storage: $storage,
    ...props
  } = t0;
  const defaultQuery = t1 === void 0 ? DEFAULT_QUERY : t1;
  const shouldPersistHeaders = t2 === void 0 ? false : t2;
  const onPrettifyQuery = t3 === void 0 ? DEFAULT_PRETTIFY_QUERY : t3;
  const dangerouslyAssumeSchemaIsValid = t4 === void 0 ? false : t4;
  const inputValueDeprecation = t5 === void 0 ? false : t5;
  const introspectionQueryName = t6 === void 0 ? "IntrospectionQuery" : t6;
  const schemaDescription = t7 === void 0 ? false : t7;
  const operationName = t8 === void 0 ? null : t8;
  const plugins = t9 === void 0 ? [] : t9;
  const defaultTheme = t10 === void 0 ? null : t10;
  const storeRef = useRef(null);
  let t12;
  if ($[0] !== transport) {
    t12 = () => transport ? new TransportHookRegistry() : null;
    $[0] = transport;
    $[1] = t12;
  } else {
    t12 = $[1];
  }
  const [registry] = useState(t12);
  const uriInstanceId = useId();
  if (storeRef.current === null) {
    const storage = new StorageAPI($storage);
    const getInitialVisiblePlugin = function getInitialVisiblePlugin2() {
      const storedValue = storage.get(STORAGE_KEY.visiblePlugin);
      const pluginForStoredValue = plugins.find((plugin) => plugin.title === storedValue);
      if (pluginForStoredValue) {
        return pluginForStoredValue;
      }
      if (storedValue) {
        storage.set(STORAGE_KEY.visiblePlugin, "");
      }
      return visiblePlugin;
    };
    const getInitialTheme = function getInitialTheme2() {
      const stored = storage.get(STORAGE_KEY.theme);
      switch (stored) {
        case "light": {
          return "light";
        }
        case "dark": {
          return "dark";
        }
        default: {
          if (typeof stored === "string") {
            storage.set(STORAGE_KEY.theme, "");
          }
          return defaultTheme;
        }
      }
    };
    const getInitialResponseView = function getInitialResponseView2() {
      const stored_0 = storage.get(STORAGE_KEY.responseView);
      if (isResponseView(stored_0)) {
        return stored_0;
      }
      if (typeof stored_0 === "string") {
        storage.set(STORAGE_KEY.responseView, "");
      }
      return "json";
    };
    const getInitialState = function getInitialState2() {
      const query = props.initialQuery ?? storage.get(STORAGE_KEY.query);
      const variables = props.initialVariables ?? storage.get(STORAGE_KEY.variables);
      const headers = props.initialHeaders ?? storage.get(STORAGE_KEY.headers);
      const {
        tabs,
        activeTabIndex
      } = getDefaultTabState({
        defaultHeaders,
        defaultQuery,
        defaultTabs,
        headers,
        query,
        shouldPersistHeaders,
        storage,
        variables
      });
      const isStored = storage.get(STORAGE_KEY.persistHeaders) !== null;
      const $shouldPersistHeaders = shouldPersistHeaders !== false && isStored ? storage.get(STORAGE_KEY.persistHeaders) === "true" : shouldPersistHeaders;
      const store = create((...t132) => {
        const args = t132;
        const storageSlice = createStorageSlice({
          storage
        })(...args);
        const editorSlice = createEditorSlice({
          activeTabIndex,
          defaultHeaders,
          defaultQuery,
          externalFragments: getExternalFragments(externalFragments),
          initialHeaders: headers ?? defaultHeaders ?? "",
          initialQuery: query ?? (activeTabIndex === 0 ? tabs[0].query : null) ?? "",
          initialVariables: variables ?? "",
          onCopyQuery,
          onSaveQuery,
          onEditOperationName,
          onPrettifyQuery,
          onTabChange,
          shouldPersistHeaders: $shouldPersistHeaders,
          tabs,
          uriInstanceId: uriInstanceId.replaceAll(/[:«»]/g, "") + "-"
        })(...args);
        const executionSlice = createExecutionSlice({
          fetcher,
          transport: transport && registry ? registry.wrap(transport) : transport,
          getDefaultFieldNames,
          overrideOperationName: operationName
        })(...args);
        const pluginSlice = createPluginSlice({
          onTogglePluginVisibility,
          referencePlugin
        })(...args);
        const schemaSlice = createSchemaSlice({
          customScalarSchemas,
          inputValueDeprecation,
          introspectionQueryName,
          onSchemaChange,
          schemaDescription
        })(...args);
        const themeSlice = createThemeSlice()(...args);
        return {
          ...storageSlice,
          ...editorSlice,
          ...executionSlice,
          ...pluginSlice,
          ...schemaSlice,
          ...themeSlice,
          actions: {
            ...editorSlice.actions,
            ...executionSlice.actions,
            ...pluginSlice.actions,
            ...schemaSlice.actions,
            ...themeSlice.actions
          }
        };
      });
      const {
        actions
      } = store.getState();
      actions.storeTabs({
        activeTabIndex,
        tabs
      });
      actions.setPlugins(plugins);
      actions.setVisiblePlugin(getInitialVisiblePlugin());
      actions.setTheme(getInitialTheme());
      actions.setResponseView(getInitialResponseView());
      if (storage.get(STORAGE_KEY.transportUpgradeBannerDismissed) === "true") {
        store.setState({
          transportUpgradeBannerDismissed: true
        });
      }
      return store;
    };
    storeRef.current = getInitialState();
  }
  let t13;
  if ($[2] !== fetcher || $[3] !== registry || $[4] !== transport) {
    t13 = () => {
      const wrappedTransport = transport && registry ? registry.wrap(transport) : transport;
      storeRef.current.setState({
        fetcher,
        transport: wrappedTransport,
        transportMethod: (wrappedTransport == null ? void 0 : wrappedTransport.method) ?? null
      });
    };
    $[2] = fetcher;
    $[3] = registry;
    $[4] = transport;
    $[5] = t13;
  } else {
    t13 = $[5];
  }
  let t14;
  if ($[6] !== fetcher || $[7] !== transport) {
    t14 = [fetcher, transport];
    $[6] = fetcher;
    $[7] = transport;
    $[8] = t14;
  } else {
    t14 = $[8];
  }
  useDidUpdate(t13, t14);
  let t15;
  let t16;
  if ($[9] !== plugins || $[10] !== visiblePlugin) {
    t15 = () => {
      const {
        actions: actions_0
      } = storeRef.current.getState();
      actions_0.setPlugins(plugins);
      actions_0.setVisiblePlugin(visiblePlugin);
    };
    t16 = [plugins, visiblePlugin];
    $[9] = plugins;
    $[10] = visiblePlugin;
    $[11] = t15;
    $[12] = t16;
  } else {
    t15 = $[11];
    t16 = $[12];
  }
  useDidUpdate(t15, t16);
  let t17;
  let t18;
  if ($[13] !== operationName) {
    t17 = () => {
      storeRef.current.setState({
        overrideOperationName: operationName ?? null
      });
    };
    t18 = [operationName];
    $[13] = operationName;
    $[14] = t17;
    $[15] = t18;
  } else {
    t17 = $[14];
    t18 = $[15];
  }
  useDidUpdate(t17, t18);
  let t19;
  if ($[16] !== dangerouslyAssumeSchemaIsValid || $[17] !== schema) {
    t19 = () => {
      const newSchema = isSchema(schema) || schema == null ? schema : isIntrospectionData(schema) ? buildClientSchema(schema) : void 0;
      const validationErrors = !newSchema || dangerouslyAssumeSchemaIsValid ? [] : validateSchema(newSchema);
      const store_0 = storeRef.current;
      store_0.setState((t202) => {
        const {
          requestCounter
        } = t202;
        return {
          requestCounter: requestCounter + 1,
          schema: newSchema,
          shouldIntrospect: !newSchema && schema !== null,
          validationErrors
        };
      });
      const {
        actions: actions_1
      } = store_0.getState();
      actions_1.introspect();
    };
    $[16] = dangerouslyAssumeSchemaIsValid;
    $[17] = schema;
    $[18] = t19;
  } else {
    t19 = $[18];
  }
  let t20;
  if ($[19] !== dangerouslyAssumeSchemaIsValid || $[20] !== fetcher || $[21] !== schema || $[22] !== transport) {
    t20 = [schema, dangerouslyAssumeSchemaIsValid, fetcher, transport];
    $[19] = dangerouslyAssumeSchemaIsValid;
    $[20] = fetcher;
    $[21] = schema;
    $[22] = transport;
    $[23] = t20;
  } else {
    t20 = $[23];
  }
  useEffect(t19, t20);
  let t21;
  let t22;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = () => {
      const runIntrospection = function runIntrospection2(event) {
        if (event.ctrlKey && event.key === "R") {
          const {
            actions: actions_2
          } = storeRef.current.getState();
          actions_2.introspect();
        }
      };
      window.addEventListener("keydown", runIntrospection);
      return () => {
        window.removeEventListener("keydown", runIntrospection);
      };
    };
    t22 = [];
    $[24] = t21;
    $[25] = t22;
  } else {
    t21 = $[24];
    t22 = $[25];
  }
  useEffect(t21, t22);
  let t23;
  if ($[26] !== children) {
    t23 = /* @__PURE__ */ jsx(GraphiQLContext.Provider, { value: storeRef, children });
    $[26] = children;
    $[27] = t23;
  } else {
    t23 = $[27];
  }
  let t24;
  if ($[28] !== registry || $[29] !== t23) {
    t24 = /* @__PURE__ */ jsx(TransportHookContext.Provider, { value: registry, children: t23 });
    $[28] = registry;
    $[29] = t23;
    $[30] = t24;
  } else {
    t24 = $[30];
  }
  return t24;
};
function useGraphiQL(selector) {
  const store = useContext(GraphiQLContext);
  if (!store) {
    throw new Error('"useGraphiQL" hook must be used within a <GraphiQLProvider> component.\nIt looks like you are trying to use the hook outside the GraphiQL provider tree.');
  }
  return useStore(store.current, useShallow(selector));
}
const useGraphiQLActions = () => {
  return useGraphiQL(_temp);
};
function getExternalFragments(externalFragments) {
  const map = /* @__PURE__ */ new Map();
  if (externalFragments) {
    if (Array.isArray(externalFragments)) {
      for (const fragment of externalFragments) {
        map.set(fragment.name.value, fragment);
      }
    } else if (typeof externalFragments === "string") {
      visit(parse(externalFragments), {
        FragmentDefinition(fragment) {
          map.set(fragment.name.value, fragment);
        }
      });
    } else {
      throw new TypeError("The `externalFragments` prop must either be a string that contains the fragment definitions in SDL or a list of `FragmentDefinitionNode` objects.");
    }
  }
  return map;
}
function _temp(state) {
  return state.actions;
}
export {
  GraphiQLProvider,
  useGraphiQL,
  useGraphiQLActions
};
//# sourceMappingURL=provider.js.map
